import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Autocomplete, Grid, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { ConfigContext } from 'components/ConfigGuard';
import {
  getMaxLengthValidationRule,
  getRequiredValidationRule,
  FormProps,
  getAddressControlName,
  countryFieldName,
} from 'util/Form';
import { defaultGridItemProps } from 'util/Layout';
import CountryFormItem from './CountryFormItem';
import countries from 'store/configs/Countries';

import externalStyles from 'components/profile/EditProfilePageView/EditProfileForm/EditProfileForm.module.scss';

interface SmartySuggestion {
  street_line: string;
  city: string;
  state: string;
  zipcode: string;
}

interface SmartyInternationalCandidate {
  street: string;
  locality: string;
  administrative_area: string;
  postal_code: string;
}

interface AddressFormSectionProps extends FormProps {
  addressFieldName?: string;
}

const NON_INTERNATIONAL_CODES = ['US', 'GU', 'PR', 'VI'];

const AddressFormSection: React.FunctionComponent<AddressFormSectionProps> = (props) => {
  const {
    inputProps,
    rowProps = defaultGridItemProps,
    required = false,
    disabled = false,
    addressFieldName = 'address',
  } = props;

  const { control, setValue, register } = useFormContext();
  const { smartyEmbeddedKey } = useContext(ConfigContext);
  const { enqueueSnackbar } = useSnackbar();

  const getControlName = useCallback(
    (label: string) => getAddressControlName(addressFieldName, label),
    [addressFieldName]
  );

  const countryValue: string = useWatch({ control, name: getControlName(countryFieldName) });

  const [region, setRegion] = useState<'us' | 'international'>('us');
  const [regionInitialized, setRegionInitialized] = useState(false);
  const [domesticSuggestions, setDomesticSuggestions] = useState<SmartySuggestion[]>([]);
  const [intlCandidates, setIntlCandidates] = useState<SmartyInternationalCandidate[]>([]);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortController = useRef<AbortController | null>(null);

  // Initialize region from existing address once form data loads
  useEffect(() => {
    if (!regionInitialized && countryValue) {
      setRegion(NON_INTERNATIONAL_CODES.includes(countryValue) ? 'us' : 'international');
      setRegionInitialized(true);
    }
  }, [countryValue, regionInitialized]);

  // Clear stale international suggestions when country changes
  useEffect(() => {
    setIntlCandidates([]);
  }, [countryValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (abortController.current) abortController.current.abort();
    };
  }, []);

  const fetchDomesticSuggestions = useCallback((query: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (query.length < 3 || !smartyEmbeddedKey) { setDomesticSuggestions([]); return; }
    debounceTimer.current = setTimeout(async () => {
      if (abortController.current) abortController.current.abort();
      abortController.current = new AbortController();
      try {
        const res = await fetch(
          `https://us-autocomplete-pro.api.smarty.com/lookup?key=${smartyEmbeddedKey}&search=${encodeURIComponent(query)}&max_results=5`,
          { signal: abortController.current.signal }
        );
        if (!res.ok) throw new Error(`Smarty US error: ${res.status}`);
        const data = await res.json();
        setDomesticSuggestions(data.suggestions ?? []);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setDomesticSuggestions([]);
        enqueueSnackbar('Unable to load address suggestions. Please try again.', { variant: 'error' });
      }
    }, 500);
  }, [enqueueSnackbar, smartyEmbeddedKey]);

  const fetchInternationalSuggestions = useCallback((query: string, countryCode: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (query.length < 3 || !smartyEmbeddedKey || !countryCode) { setIntlCandidates([]); return; }
    debounceTimer.current = setTimeout(async () => {
      if (abortController.current) abortController.current.abort();
      abortController.current = new AbortController();
      try {
        const res = await fetch(
          `https://international-autocomplete.api.smarty.com/v2/lookup?key=${smartyEmbeddedKey}&search=${encodeURIComponent(query)}&country=${countryCode}&max_results=5`,
          { signal: abortController.current.signal }
        );
        if (!res.ok) throw new Error(`Smarty International error: ${res.status}`);
        const data = await res.json();
        setIntlCandidates(data.candidates ?? []);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setIntlCandidates([]);
        enqueueSnackbar('Unable to load address suggestions. Please try again.', { variant: 'error' });
      }
    }, 500);
  }, [enqueueSnackbar, smartyEmbeddedKey]);

  const handleRegionChange = useCallback((_: any, newRegion: 'us' | 'international' | null) => {
    if (!newRegion) return;
    setRegion(newRegion);
    setDomesticSuggestions([]);
    setIntlCandidates([]);
    setValue(getControlName('street1'), '');
    setValue(getControlName('street2'), '');
    setValue(getControlName('city'), '');
    setValue(getControlName('stateRegion'), '');
    setValue(getControlName('postalCode'), '');
    if (newRegion === 'us') {
      setValue(getControlName('countryCode'), 'US');
      setValue(getControlName('country'), 'United States');
    } else {
      setValue(getControlName('countryCode'), '');
      setValue(getControlName('country'), '');
    }
  }, [getControlName, setValue]);

  return (
    <Grid container>
      {/* Region Toggle */}
      <Grid {...rowProps} xs={12}>
        <Typography variant={'h5'} className={externalStyles.fieldName}>
          Region
        </Typography>
        <ToggleButtonGroup
          value={region}
          exclusive
          onChange={handleRegionChange}
          disabled={disabled}
          sx={{ mt: '16px', mb: '24px' }}
        >
          <ToggleButton value="us">United States</ToggleButton>
          <ToggleButton value="international">International</ToggleButton>
        </ToggleButtonGroup>
      </Grid>

      {/* International: Country Selector */}
      {region === 'international' && (
        <Grid {...rowProps} xs={12}>
          <Typography variant={'h5'} className={externalStyles.fieldName}>
            Country
          </Typography>
          <CountryFormItem {...props} addressFieldName={addressFieldName} />
        </Grid>
      )}

      {/* Address Autocomplete */}
      <Grid {...rowProps} xs={12}>
        <Typography variant={'h5'} className={externalStyles.fieldName}>
          Address
        </Typography>
        <Controller
          name={getControlName('street1')}
          control={control}
          rules={{
            required: getRequiredValidationRule('address', false, required),
            maxLength: getMaxLengthValidationRule(150),
          }}
          render={({ field, fieldState }) => (
            <Autocomplete
              freeSolo
              filterOptions={(x) => x}
              options={(region === 'us' ? domesticSuggestions : intlCandidates) as (SmartySuggestion | SmartyInternationalCandidate)[]}
              getOptionLabel={(o) => {
                if (typeof o === 'string') return o;
                if (region === 'us') {
                  const s = o as SmartySuggestion;
                  return [s.street_line, s.city, s.state, s.zipcode].filter(Boolean).join(' ');
                }
                const c = o as SmartyInternationalCandidate;
                return [c.street, c.locality, c.administrative_area, c.postal_code].filter(Boolean).join(' ');
              }}
              inputValue={field.value ?? ''}
              onInputChange={(_, value, reason) => {
                // Skip 'reset' (option selected) — onChange already set the correct street_line
                if (reason === 'reset') return;
                field.onChange(value);
                if (value === '') {
                  setValue(getControlName('city'), '');
                  setValue(getControlName('stateRegion'), '');
                  setValue(getControlName('postalCode'), '');
                } else if (reason === 'input') {
                  region === 'us'
                    ? fetchDomesticSuggestions(value)
                    : fetchInternationalSuggestions(value, countryValue);
                }
              }}
              onChange={(_, value) => {
                if (value && typeof value !== 'string') {
                  if (region === 'us') {
                    const s = value as unknown as SmartySuggestion;
                    field.onChange(s.street_line);
                    setValue(getControlName('city'), s.city);
                    setValue(getControlName('stateRegion'), s.state);
                    setValue(getControlName('postalCode'), s.zipcode);
                    setValue(getControlName('countryCode'), 'US');
                    setValue(getControlName('country'), 'United States');
                  } else {
                    const c = value as unknown as SmartyInternationalCandidate;
                    field.onChange(c.street);
                    setValue(getControlName('city'), c.locality);
                    setValue(getControlName('stateRegion'), c.administrative_area);
                    setValue(getControlName('postalCode'), c.postal_code);
                    const fullName = countries.find((x) => x.value === countryValue)?.text ?? '';
                    setValue(getControlName('country'), fullName);
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...inputProps}
                  {...params}
                  fullWidth
                  disabled={disabled}
                  placeholder="Start typing your address..."
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  className={externalStyles.inputField}
                  inputProps={{ ...params.inputProps, 'data-testid': 'street1-input' }}
                  data-testid="street1-text-field"
                />
              )}
            />
          )}
        />
      </Grid>

      {/* Apt/Unit */}
      <Grid {...rowProps} xs={12}>
        <Typography variant={'h5'} className={externalStyles.fieldName}>
          Apt, Unit, Suite, etc (Optional)
        </Typography>
        <Controller
          render={({ field }) => (
            <TextField
              {...field}
              {...inputProps}
              disabled={disabled}
              inputProps={{ 'data-testid': 'street2-input' }}
              data-testid={'street2-text-field'}
              className={externalStyles.inputField}
              FormHelperTextProps={{ 'data-testid': 'street2-helper-text' } as any}
            />
          )}
          name={getControlName('street2')}
          control={control}
          rules={{ required: getRequiredValidationRule('address', false, required) }}
        />
      </Grid>

      {/* Hidden fields — set via setValue when suggestion is selected */}
      <input type="hidden" {...register(getControlName('city'))} />
      <input type="hidden" {...register(getControlName('stateRegion'))} />
      <input type="hidden" {...register(getControlName('postalCode'))} />
      {region === 'us' && <input type="hidden" {...register(getControlName('countryCode'))} />}
      <input type="hidden" {...register(getControlName('country'))} />
    </Grid>
  );
};

export default AddressFormSection;
